import {useDatePickerInput} from '@/components/atoms/DatePickerInput/datePickerInput-hook'
import {DATE_VALIDATION_MESSAGE} from '@/utils/constants/commonConstants'
import {DatePickerInput as PepsicoDsDatePicker} from 'pepsico-ds'
import {useEffect, useRef} from 'react'

/**
 * DatePickerInput component renders a date picker input field with validation and custom styling.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.value - The selected date value.
 * @param {function} props.updateValue - The function to update the selected date value.
 * @param {Object} props.uiSchema - The UI schema object for additional configurations.
 * @param {string} props.uiSchema.label - The label for the date picker input.
 * @param {string} [props.uiSchema.placeholder='mm/dd/yyyy'] - The placeholder text for the date picker input.
 * @param {boolean} [props.uiSchema.required=true] - Whether the date picker input is required.
 * @param {string} props.uiSchema.className - The CSS class name for the date picker input.
 * @param {string} [props.uiSchema.feedbackType=''] - The feedback type for the date picker input.
 * @param {string} [props.id=''] - Optional - The id for the date picker input.
 *
 * @returns {JSX.Element} The rendered DatePickerInput component.
 */
export default function DatePickerInput({value, updateValue, uiSchema, id = '', isMexico}) {
  const {
    label,
    placeholder = 'mm/dd/yyyy',
    required = true,
    className,
    feedbackType = '',
    isDark = false,
    validationRules = [],
    disabled = false,
    disabledHelperText = DATE_VALIDATION_MESSAGE.END_DATE_HELPER_TEXT,
    dependentDate,
    format,
  } = uiSchema

  const {validateDates} = useDatePickerInput(label, validationRules, isMexico)
  const ref = useRef()
  const validationMessage = value ? validateDates(value) || '' : ''

  useEffect(() => {
    if (dependentDate) {
      updateValue(value, Boolean(validationMessage))
    } else if (!value) {
      updateValue(value, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentDate, value])

  const handleDateSelection = val => {
    if (!val) {
      updateValue(val, false)
      return
    }

    updateValue(val, Boolean(validateDates(val)))
  }

  const endYear = new Date().getFullYear() + 5
  return (
    <div ref={ref}>
      <PepsicoDsDatePicker
        id={`date_picker_input` + (id ? `_${id}` : '')}
        data-testid={`date_picker_input` + (id ? `_${id}` : '')}
        feedbackType={feedbackType}
        error={Boolean(validationMessage)}
        helperText={disabled ? disabledHelperText : validationMessage}
        label={label}
        placeholder={placeholder} // TODO - Country wise date format
        required={required}
        selected={value}
        format={format}
        isDark={isDark}
        className={'date_picker_input_display' + className}
        setSelected={val => {
          if (disabled) return
          handleDateSelection(val)
        }}
        disabled={disabled}
        endYear={endYear}
      />
    </div>
  )
}
